import { claimAudioFocus, onOtherAudioFocus } from '@/audio/focus'
import {
  registerMediaHandlers,
  setMediaMetadata,
  setPlaybackState,
  setPositionState,
} from '@/audio/mediaSession'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Music, Pause, Play, SkipBack, SkipForward } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import { Button, IconButton } from '@/components/ui'
import { Cover } from '@/components/Cover'
import { TrackRow } from '@/components/TrackRow'
import { nextPlayable, type SharedCollection } from './types'

export function GuestPage() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const audio = useRef<HTMLAudioElement>(null)
  const failed = useRef(new Set<number>())
  const [collection, setCollection] = useState<SharedCollection | null>(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [index, setIndex] = useState(-1)
  const [playing, setPlaying] = useState(false)
  const [busy, setBusy] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const entries = collection?.entries ?? []
  const current = entries[index]
  const base = `/guest/${id}`
  async function refresh() {
    try {
      const data = location.hash
        ? await api.post<SharedCollection>(`${base}/exchange`, { token: location.hash.slice(1) })
        : await api.get<SharedCollection>(`${base}/collection`)
      setCollection(data)
      setError('')
      if (current) {
        const nextIndex = data.entries.findIndex(
          (e) => e.id === current.id && e.availability === 'playable',
        )
        setIndex(nextIndex)
        if (nextIndex < 0) {
          audio.current?.pause()
          setNotice('This song was removed or is no longer available.')
        }
      }
      failed.current.clear()
    } catch (error) {
      audio.current?.pause()
      setCollection(null)
      setError(
        error instanceof ApiError && error.status === 429
          ? error.message
          : 'This link is unavailable or has expired. Ask the sender for a current link.',
      )
    }
  }
  useEffect(() => {
    const element = audio.current
    element?.pause()
    setIndex(-1)
    setCollection(null)
    failed.current.clear()
    const robots = document.createElement('meta')
    robots.name = 'robots'
    robots.content = 'noindex, nofollow, noarchive'
    document.head.appendChild(robots)
    void refresh()
    // One local element; never use the personal queue, history, auth queries, or caches.
    return () => {
      element?.pause()
      element?.removeAttribute('src')
      element?.load()
      robots.remove()
    }
    // Only exchange when the link changes; refresh is an explicit user action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.hash])
  useEffect(() => onOtherAudioFocus(() => audio.current?.pause()), [])
  useEffect(() => {
    setMediaMetadata(current ? { title: current.title, artistName: current.artist } : null)
    return () => setMediaMetadata(null)
  }, [current])
  useEffect(() => {
    setPlaybackState(playing)
    setPositionState(time, duration)
  }, [playing, time, duration])
  useEffect(
    () =>
      registerMediaHandlers({
        play: () => {
          if (!current) start(nextPlayable(entries, -1))
          else void audio.current?.play().catch(() => setNotice('Tap Play to resume.'))
        },
        pause: () => audio.current?.pause(),
        previous: () => advance(-1),
        next: () => advance(),
        seekTo: (seconds) => {
          if (audio.current && Number.isFinite(seconds))
            audio.current.currentTime = Math.max(0, Math.min(seconds, audio.current.duration || 0))
        },
        stop: () => audio.current?.pause(),
      }),
    [collection, index],
  )
  function start(next: number) {
    if (!audio.current) return
    if (next < 0 || next >= entries.length) {
      audio.current.pause()
      setPlaying(false)
      setNotice('End of this shared collection.')
      return
    }
    if (entries[next].availability !== 'playable') {
      setNotice(entries[next].reason)
      return
    }
    setIndex(next)
    setNotice('')
    setTime(0)
    audio.current.src = `/api/v1${base}/tracks/${entries[next].track_id}/audio`
    void audio.current
      .play()
      .catch(() => setNotice('Playback could not start. Tap Play to try again.'))
  }
  function advance(direction = 1) {
    start(nextPlayable(entries, index, direction, failed.current))
  }
  function toggle() {
    if (!current) {
      start(nextPlayable(entries, -1))
      return
    }
    if (playing) audio.current?.pause()
    else
      void audio.current
        ?.play()
        .catch(() => setNotice('Playback is unavailable. Refresh this link.'))
  }
  async function mediaFailed() {
    if (!current || !audio.current) return
    const source = audio.current.src
    failed.current.add(current.id)
    audio.current.pause()
    try {
      // HTMLMediaElement hides HTTP status. A scoped HEAD distinguishes a
      // resource limit from a broken song without downloading more audio.
      const response = await fetch(source, {
        method: 'HEAD',
        credentials: 'same-origin',
        cache: 'no-store',
      })
      if (audio.current?.src !== source) return
      if (response.status === 429) {
        setNotice('Sharing is busy or its traffic allowance has been reached. Try again later.')
        return
      }
      if (response.status === 404) {
        await refresh()
        setNotice('This song is no longer available. The collection has been refreshed.')
        return
      }
    } catch {
      setNotice('Cannot reach the server. Check your connection, then refresh this link.')
      return
    }
    advance()
  }
  async function copy() {
    setBusy(true)
    try {
      const me = await api.get<{ id: number } | null>('/auth/me')
      if (!me) {
        void navigate('/login', { state: { from: `/share/${id}` } })
        return
      }
      const result = await api.post<{
        existing?: boolean
        playlist_id?: number
        ready: number
        preparing: number
        unavailable: number
      }>(`${base}/copy`)
      setNotice(
        result.existing
          ? 'This is already your collection.'
          : `Independent copy saved: ${result.ready} ready, ${result.preparing} preparing, ${result.unavailable} unavailable. Later sender edits will not change your copy.`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated)
        void navigate('/login', { state: { from: `/share/${id}` } })
      else setNotice((error as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <main className="mx-auto min-h-dvh max-w-3xl px-4 pb-10 pt-8 sm:px-8">
      <div className="mb-8 flex items-center gap-2 text-text-muted">
        <Music className="h-5 w-5" aria-hidden="true" />
        <span className="font-semibold">Tunes</span>
        <span className="text-xs">Shared music</span>
      </div>
      {error ? (
        <div role="alert" className="space-y-4">
          <h1 className="text-2xl font-semibold">Link unavailable</h1>
          <p>{error}</p>
          <Button variant="secondary" onClick={() => void refresh()}>
            Try again
          </Button>
        </div>
      ) : !collection ? (
        <p role="status">Opening shared music…</p>
      ) : (
        <>
          <header className="flex items-end gap-5">
            <Cover
              src={entries.find((e) => e.cover_url)?.cover_url}
              className="h-28 w-28 shrink-0 sm:h-40 sm:w-40"
            />
            <div className="min-w-0">
              <p className="text-xs text-text-muted">Unlisted {collection.kind}</p>
              <h1 className="mt-1 break-words text-2xl font-semibold sm:text-3xl">
                {collection.title}
              </h1>
              <p className="mt-2 text-sm text-text-muted">{collection.description}</p>
              <p className="mt-2 text-xs text-text-muted">
                {collection.counts.playable} playable · {collection.counts.preparing} preparing ·{' '}
                {collection.counts.unavailable} unavailable
              </p>
            </div>
          </header>
          <p className="my-4 text-xs text-text-muted">
            Anyone with the link can listen.{' '}
            {collection.kind === 'playlist' ? 'This playlist follows the sender’s edits. ' : ''}
            {collection.expires_at
              ? `Expires ${new Date(collection.expires_at).toLocaleDateString()}.`
              : 'Available until revoked.'}
          </p>
          <section
            aria-label="Shared player"
            className="my-5 rounded-card border border-border bg-surface-raised p-4"
          >
            <p className="mb-3 truncate text-sm">
              {current ? `${current.title} · ${current.artist}` : 'Ready when you are'}
            </p>
            <div className="flex items-center gap-3">
              <IconButton
                aria-label="Previous song"
                disabled={nextPlayable(entries, index, -1) < 0}
                onClick={() => advance(-1)}
              >
                <SkipBack className="h-5 w-5" />
              </IconButton>
              <Button onClick={toggle} disabled={!collection.counts.playable}>
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {playing ? 'Pause' : 'Play'}
              </Button>
              <IconButton
                aria-label="Next song"
                disabled={nextPlayable(entries, index) < 0}
                onClick={() => advance()}
              >
                <SkipForward className="h-5 w-5" />
              </IconButton>
              <span className="ml-auto text-xs tabular-nums">
                {Math.floor(time)} / {Math.floor(duration)}s
              </span>
            </div>
            <input
              className="mt-4 min-h-11 w-full accent-accent"
              aria-label="Seek"
              type="range"
              min={0}
              max={duration || 1}
              step={0.1}
              value={Math.min(time, duration || 1)}
              disabled={!current || !duration}
              onChange={(e) => {
                if (audio.current) audio.current.currentTime = Number(e.target.value)
              }}
            />
          </section>
          <div role="status" className="my-3 text-sm text-text-muted">
            {notice}
          </div>
          <h2 className="mb-2 text-sm font-semibold">Shared queue</h2>
          <ol>
            {entries.map((entry, i) => (
              <li key={entry.id}>
                <TrackRow
                  title={entry.title}
                  artistName={entry.artist}
                  coverUrl={entry.cover_url}
                  durationMs={entry.duration_ms}
                  includeArtistMenu={false}
                  isActive={index === i}
                  isPlaying={playing && index === i}
                  onPlay={() => start(i)}
                />
                {entry.availability !== 'playable' && (
                  <p className="mb-2 px-3 text-xs text-text-muted">{entry.reason}</p>
                )}
              </li>
            ))}
          </ol>
          {!entries.length && <p className="py-6 text-sm">This collection is empty.</p>}
          <div className="mt-6 flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => void refresh()}>
              Refresh collection
            </Button>
            {entries.length > 0 && (
              <Button variant="secondary" loading={busy} onClick={() => void copy()}>
                Save a copy to my library
              </Button>
            )}
          </div>
          <p className="mt-3 text-xs text-text-muted">
            Saving needs sign-in and creates your own independent copy, subject to your account’s
            sources and storage allowance. Listening does not save songs or change the sender’s
            library.
          </p>
        </>
      )}
      <audio
        ref={audio}
        preload="none"
        onPlay={() => {
          claimAudioFocus()
          setPlaying(true)
        }}
        onPause={() => setPlaying(false)}
        onTimeUpdate={() => setTime(audio.current?.currentTime ?? 0)}
        onDurationChange={() =>
          setDuration(Number.isFinite(audio.current?.duration) ? audio.current!.duration : 0)
        }
        onEnded={() => advance()}
        onError={() => void mediaFailed()}
      />
    </main>
  )
}
